import { useState, useEffect, useMemo, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPracticeListRequest,
  setPaperName,
  setPlannerDetails,
  setSelectedType,
  setTestDetails,

} from "../../../redux/exam/examSlice";
import { setCurrentCuetPractice } from "../../../redux/cuet/CuetSlice";
import { useHistory } from "react-router-dom";
import { navigateFromPracticeOrigin } from "../../../custom_utilities/dashboardBannerNavigation";
import "./PracticeDashboard.css";
import images from "../../../utilities/images/images";
import Modal from "../../../shared/components/Modal/Modal";
import ProgressBar from "../../../custom_utilities/ProgressBar/ProgressBar";
import {
  resolvePyqFormatFromName,
  shouldSendFormatParam,
} from "../../../custom_utilities/pyqFormatUtils";

const PracticeDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { practiceList, testDetails, loading, error } = useSelector(
    (state) => state.examSlice
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSublist, setSelectedSublist] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState("");

  const userPreference = useSelector((state) => state.userPreference);
  const targetId = Number(userPreference?.targetDetails?.[0]?.target_id);
  const isHomeworkVisible = targetId !== 5;

  const hasHomeworkItem = (items = []) =>
    items.some((item) =>
      String(item?.name || "").toLowerCase().includes("homework")
    );

  const enrichSectionWithHomework = (section) => {
    if (!section || !isHomeworkVisible) return section;

    const data = Array.isArray(section.data) ? section.data : [];
    if (hasHomeworkItem(data)) return section;

    return {
      ...section,
      data: [
        ...data,
        {
          id: "homework",
          name: "Homework",
          icon: images.pendingIcon,
        },
      ],
    };
  };

  const enhanceYourPracticeSection = useMemo(
    () => enrichSectionWithHomework(practiceList?.enhance_your_practice),
    [practiceList?.enhance_your_practice, isHomeworkVisible]
  );

  const isCuetPracticeBoardItem = (item) => {
    const itemId = Number(item?.id);
    const name = String(item?.name || "").toLowerCase();
    if ([16, 17, 18].includes(itemId)) return true;
    if (name.includes("board question bank")) return true;
    if (name.includes("cuet question bank")) return true;
    if (name.includes("ncert e-book")) return true;
    return false;
  };

  const syncPyqFormatState = (name) => {
    const pyqFormat = resolvePyqFormatFromName(name);
    if (pyqFormat) {
      dispatch(setSelectedType(pyqFormat));
      dispatch(
        setTestDetails({
          ...(testDetails || {}),
          format: pyqFormat,
        })
      );
      return;
    }

    const nextDetails = { ...(testDetails || {}) };
    delete nextDetails.format;
    dispatch(setTestDetails(nextDetails));
  };

  const navigateToCuetBoard = (item) => {
    dispatch(
      setCurrentCuetPractice({
        id: item?.id,
        name: item?.name,
      })
    );
    navigateFromPracticeOrigin(history, "/cuet+board");
  };

  // Fetch practice list when the component mounts
  useEffect(() => {
    dispatch(fetchPracticeListRequest());
  }, [dispatch]);

  const handleItemClick = (item, e, section) => {
    e.stopPropagation();
    dispatch(
      setTestDetails({
        ...(testDetails || {}),
        returnTo: "/practice",
        redirectedFrom: "",
      })
    );
    dispatch(setPaperName(item.name));
    const itemName = String(item?.name || "").toLowerCase();
    const isAdaptiveQuestionBank = itemName.includes("adaptive question bank");
    const isUnlimitedQuestionBank = itemName.includes("unlimited question bank");

    // Adaptive Question Bank should directly open submit page (skip test listing/modal flow).
    if (isAdaptiveQuestionBank) {
      navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank", { item });
      return;
    }

    if (isUnlimitedQuestionBank) {
      navigateFromPracticeOrigin(history, "/question_bank");
      return;
    }

    if (itemName.includes("homework")) {
      navigateFromPracticeOrigin(history, "/homework");
      return;
    }

    if (itemName.includes("mistake book")) {
      navigateFromPracticeOrigin(history, "/practice/mistake-book");
      return;
    }

    if (item?.type) {
      dispatch(setSelectedType(item.type));
      if (shouldSendFormatParam(item.type)) {
        dispatch(
          setTestDetails({
            ...testDetails,
            format: item.type,
          })
        );
      } else {
        const nextDetails = { ...testDetails };
        delete nextDetails.format;
        dispatch(setTestDetails(nextDetails));
      }
    }

    if (isCuetPracticeBoardItem(item)) {
      navigateToCuetBoard(item);
      return;
    }

    if (itemName.includes("pyq")) {
      if (item.sublist) {
        setSelectedSublist(item);
        setSelectedItemName(item.name);
        setModalOpen(true);
      }
      return;
    }
    if (
      item?.sublist &&
      Array.isArray(item.sublist) &&
      item.sublist.length > 0
    ) {
      setSelectedSublist(item);
      setSelectedItemName(item.name);
      setModalOpen(true);
      return;
    }

    const normalizedName = item?.name?.toLowerCase?.() || "";
    if (normalizedName.includes("adaptive question bank")) {
      navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank");
      return;
    }

    if (section === "main_practice_dashboard") {
      const name = item?.name?.toLowerCase();
      const itemId = Number(item?.id);
      const isExamCrackerMain =
        itemId === 9 || name.includes("exam cracker (jee mains)");
      const isExamCrackerAdvanced =
        itemId === 8 ||
        name.includes("exam cracker (jee advanced)") ||
        name.includes("exam cracker (jee advance)");
      if (isExamCrackerMain) {
        const paperTypeId = itemId || 9;
        dispatch(setSelectedType("main"));
        dispatch(
          setTestDetails({
            ...(testDetails || {}),
            format: "main",
          })
        );
        dispatch(
          setPlannerDetails({
            planner_id: item?.planner_id || "",
            planner_type_id: item?.planner_type_id || "",
            category_id: paperTypeId,
            id: paperTypeId,
            type: "topic wise",
          })
        );
        navigateFromPracticeOrigin(history, "/practice/topic-wise");
        return;
      }
      if (isExamCrackerAdvanced) {
        const paperTypeId = itemId || 8;
        dispatch(setSelectedType("adv"));
        dispatch(
          setTestDetails({
            ...(testDetails || {}),
            format: "adv",
          })
        );
        dispatch(
          setPlannerDetails({
            planner_id: item?.planner_id || "",
            planner_type_id: item?.planner_type_id || "",
            category_id: paperTypeId,
            id: paperTypeId,
            type: "topic wise",
          })
        );
        navigateFromPracticeOrigin(history, "/practice/topic-wise");
        return;
      }
      const isExamCrackerNeet =
        itemId === 6 ||
        (name.includes("exam cracker") && name.includes("neet"));
      if (isExamCrackerNeet) {
        const paperTypeId = 6;
        dispatch(setSelectedType("main"));
        dispatch(
          setTestDetails({
            ...(testDetails || {}),
            format: "main",
          })
        );
        dispatch(
          setPlannerDetails({
            planner_id: item?.planner_id || "",
            planner_type_id: item?.planner_type_id || "",
            category_id: paperTypeId,
            id: paperTypeId,
            type: "topic wise",
          })
        );
        navigateFromPracticeOrigin(history, "/practice/topic-wise");
        return;
      }
    }

    if (section === "enhance_your_practice") {
      const name = itemName;
      if (name.includes("conceptual problem sheets")) {
        navigateFromPracticeOrigin(history, "/cps");
        return;
      }
      if (name.includes("adaptive cps")) {
        navigateFromPracticeOrigin(history, "/create-adaptive-cps");
        return;
      }
      if (name.includes("exams")) {
        navigateFromPracticeOrigin(history, "/exam");
        return;
      }
      if (name.includes("bitsat")) {
        syncPyqFormatState(item.name);

        dispatch(
          setPlannerDetails({
            planner_id: item.planner_id,
            planner_type_id: item.planner_type_id,
            category_id: item.category_id || "",
            id: item.id || "",
            type: item.type || "",
          })
        );

        navigateFromPracticeOrigin(history, "/practice/paper-listing");

        return;
      }
      if (name.includes("ncert")) {
        navigateFromPracticeOrigin(history, "/ncert");
        return;
      }
      if (name.includes("adaptive question bank")) {
        navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank");
        return;
      }
      if (name.includes("mock")) {
        syncPyqFormatState(item.name);

        dispatch(
          setPlannerDetails({
            planner_id: item.planner_id,
            planner_type_id: item.planner_type_id,
            category_id: item.category_id || "",
            id: item.id || "",
            type: item.type || "",
          })
        );

        navigateFromPracticeOrigin(history, "/practice/paper-listing");
        return;
      }
      if (name.includes("unlimited question bank")) {
        navigateFromPracticeOrigin(history, "/question_bank");
        return;
      }
    }

    if (item?.planner_id && item?.planner_type_id) {
      syncPyqFormatState(item.name);

      dispatch(
        setPlannerDetails({
          planner_id: item.planner_id,
          planner_type_id: item.planner_type_id,
          category_id: item.category_id || "",
          id: item.id || "",
          type: item.type || "",
        })
      );

      if (section === "main_practice_dashboard") {
        navigateFromPracticeOrigin(history, "/practice/paper-listing");
      } else {
        navigateFromPracticeOrigin(history, "/subjects");
      }
      return;
    }

    navigateFromPracticeOrigin(history, "/practice/paper-listing");
  };

  // Handle item click inside the modal
  const handleModalItemClick = (subitem) => {
    setModalOpen(false);
    dispatch(
      setTestDetails({
        ...(testDetails || {}),
        returnTo: "/practice",
        redirectedFrom: "",
      })
    );

    if (isCuetPracticeBoardItem(selectedSublist)) {
      navigateToCuetBoard(selectedSublist);
      return;
    }

    // If modal was opened from Adaptive Question Bank card, skip listing and open submit directly.
    if (
      String(selectedItemName || "")
        .toLowerCase()
        .includes("adaptive question bank")
    ) {
      navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank", { item: subitem });
      return;
    }

    syncPyqFormatState(selectedItemName);

    dispatch(
      setPlannerDetails({
        planner_id: subitem.planner_id,
        planner_type_id: subitem.planner_type_id,
        category_id: subitem.category_id || "",
        id: subitem.id || "",
        type: subitem?.type?.toLowerCase(),
      })
    );

    const type = subitem?.type?.toLowerCase();

    if (type === "year wise") {
      navigateFromPracticeOrigin(history, "/practice/paper-listing?type=Year Wise");
    } else if (type === "topic wise") {
      navigateFromPracticeOrigin(history, "/practice/topic-wise");
    } else {
      navigateFromPracticeOrigin(history, "/practice/paper-listing");
    }
  };

  // Handle section click for navigation
  const handleSectionClick = (e, section) => {
    if (e.target.closest(".dashboard-grid-item")) return;

    if (section === "main_practice_dashboard") {
      navigateFromPracticeOrigin(history, "/practice/paper-listing");
    }

    if (section === "enhance_your_practice") {
      navigateFromPracticeOrigin(history, "/subjects");
    }

    if (section === "homework") {
      navigateFromPracticeOrigin(history, "/homework");
    }
  };

  // Render section with items
  const renderSection = (section, sectionType) => {
    if (!section) return null;

    const visibleItems = Array.isArray(section?.data) ? section.data : [];

    return (
      <>
        <h2 className="section-title" style={{ paddingLeft: "20px" }}>
          {section?.title}
        </h2>
        <div
          className="section"
          style={{ paddingLeft: "20px" }}
          onClick={(e) => handleSectionClick(e, sectionType)}
        >
          <div className="dashboard-grid">
            {visibleItems.map((item, index) => (
              <div
                key={item?.id + index}
                className="dashboard-grid-item"
                onClick={(e) => handleItemClick(item, e, sectionType)}
                style={{ cursor: "pointer" }}
              >
                <div className="icon-badge-wrapper">
                  <div className="icon-container">
                    <img
                      src={item?.icon || item?.pimage}
                      alt={item?.name || item?.sublistName}
                      className="icon"
                    />
                  </div>
                  {item?.tag && (
                    <span
                      className="item-tag"
                      style={{
                        backgroundColor: item?.tag_color,
                        color: "#fff",
                      }}
                    >
                      {item?.tag}
                    </span>
                  )}
                </div>

                <div className="item-details">
                  <p className="item-name">{item?.name || item?.sublistName}</p>
                  {item?.progress_percent !== null &&
                    item?.progress_percent !== undefined && (
                      <div className="progress-bar-bg">
                        <div
                          className="progress-bar-fill"
                          style={
                            Number(item.progress_percent) === 0
                              ? {
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: "#D5D7DA",
                              }
                              : {
                                width: `${Number(item.progress_percent)}%`,
                              }
                          }
                        />
                      </div>
                    )}

                </div>

              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  // Loading and error states
  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="practice-dashboard-wrapper">
      <Fragment>
        {/* Navigation Header */}
        <div id="practice-nav">
          <div>
            <img
              style={{ width: 22, height: 22, cursor: "pointer" }}
              src={images.new_backarrow}
              alt="Back"
              onClick={() => history.push("/dashboard")}
            />
          </div>
          <div style={{ marginLeft: "25px" }} id="practice-title">
            Practice
          </div>
        </div>

        {/* Main Practice Dashboard */}
        <div onClick={(e) => handleSectionClick(e, "main_practice_dashboard")}>
          {practiceList?.main_practice_dashboard && (
            <div>
              {renderSection(
                practiceList?.main_practice_dashboard,
                "main_practice_dashboard"
              )}
            </div>
          )}
        </div>

        {/* Enhance Your Practice Section */}
        <div onClick={(e) => handleSectionClick(e, "enhance_your_practice")}>
          {enhanceYourPracticeSection && (
            <div>
              {renderSection(
                enhanceYourPracticeSection,
                "enhance_your_practice"
              )}
            </div>
          )}
        </div>

        {/* Homework Section (when returned as a separate block by API) */}
        <div onClick={(e) => handleSectionClick(e, "homework")}>
          {practiceList?.homework && (
            <div>
              {renderSection(
                enrichSectionWithHomework(practiceList.homework),
                "homework"
              )}
            </div>
          )}
        </div>

        {/* Modal for Sublist Items */}
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          width="auto"
          height="auto"
          backgroundColor="#fff"
          closeAboveModal
          marginTop="0"
        >
          <div className="modal-item-content">
            {selectedItemName && (
              <h3 className="modal-title" id="modal-title-left">
                {selectedItemName}
              </h3>
            )}

            {selectedSublist?.sublist?.map((subitem, index) => (
              <div
                key={subitem.id + index}
                className="modal-item"
                onClick={() => handleModalItemClick(subitem)}
                style={{ cursor: "pointer" }}
              >
                <div className="modal-item-header">
                  <div className="modal-icon-container">
                    <img
                      src={subitem?.image}
                      alt={subitem?.sublistName}
                      className="modal-icon"
                    />
                  </div>

                  <div className="modal-item-details">
                    <p
                      className="modal-item-name"
                      style={{ marginBottom: "0px" }}
                    >
                      {subitem?.sublistName}
                    </p>

                    <p
                      className="modal-item-description"
                      style={{ marginBottom: "0px" }}
                    >
                      {subitem?.description}
                    </p>

                    {/* <ProgressBar
                    progress={subitem?.progress_percent}
                    color={subitem?.color}
                    height={"10px"}
                  /> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      </Fragment>
    </div>
  );
};

export default PracticeDashboard;
